import { Component } from '@angular/core';
import { QueryService } from '@pt/api-client';
import { AbstractWidgetDirective } from '../../abstract-widget.directive';
import { ActionsSubject, Store } from '@ngrx/store';
import { UiWidget, WidgetConfiguration } from '@pt/pt-models';
import { TranslateService } from '@ngx-translate/core';
import { WidgetAppSummaryV0ArgsDto, WidgetAppSummaryV0ArgsHeader } from './models/widget-app-summary-v0-args.dto';
import { WidgetAppSummaryV0PreviewDto } from './models/widget-app-summary-v0-preview.dto';
import { WidgetDialogService } from '../../../services/widget-dialog.service';
import { WidgetAppSummaryV0QueryResponseDto } from './models/widget-app-summary-v0-query-response.dto';
import { WidgetUtils } from '../../widget.utils';
import {
  WidgetAppSummaryV0ValueAppDto,
  WidgetAppSummaryV0ValueDto
} from './models/widget-app-summary-v0-value-app.dto';
import { NgxPermissionsService } from 'ngx-permissions';
import { WidgetAppSummaryV0ResponseValue } from './models/widget-app-summary-v0-response-value';
import { WidgetAppSummaryV0RidMappingDto } from './models/widget-app-summary-v0-rid-mapping.dto';
import { TreatmentWarning } from 'it/primatoo/openapi/api/org/schemas/treatment/TreatmentWarning';
import { WidgetAppSummaryUtils } from './widget-app-summary-utils';

@Component({
  selector: 'pt-widget-app-summary-v0',
  template: `
    <div class="position-relative" *ngIf="internalStatus.enabled">
      <pt-widget-disabled
        *ngIf="internalStatus.isDisabled"
        [showLoader]="internalStatus.viewMode === 'LOADING'"
      >
      </pt-widget-disabled>

      <pt-widget-header
        class="w-100"
        [label]="widget?.label"
        [help]="widget?.help"
        [comment]="comment"
        [unsavedComment]="unsavedComment"
        [showCancelButton]="internalStatus.showCancelButton"
        [showCommentButton]="internalStatus.showCommentButton"
        [showEditButton]="internalStatus.showEditButton"
        [showReloadButton]="internalStatus.showReloadButton"
        [showSaveButton]="internalStatus.showSaveButton"
        [showOptButton]="internalStatus.showOptButton"
        [disabledCancelButton]="internalStatus.disabledCancelButton"
        [disabledCommentButton]="internalStatus.disabledCommentButton"
        [disabledEditButton]="internalStatus.disabledEditButton"
        [disabledOptButton]="internalStatus.disabledOptButton"
        [disabledReloadButton]="internalStatus.disabledReloadButton"
        [disabledSaveButton]="internalStatus.disabledSaveButton"
        [loading]="internalStatus.isLoading"
        (clickSaveWidget)="saveWidget()"
        (clickResetWidget)="resetWidget(widget)"
        (clickEditWidget)="dispatchEditState()"
        (clickReloadWidget)="reloadWidget(widget)"
        (clickCreateOpt)="createOptWidget()"
        (clickCommentWidget)="clickCommentWidget()"
      >
      </pt-widget-header>

      <ng-container *ngIf="!internalStatus.showOptButton">
        <pt-widget-app-summary-v0-warning
          [editable]="internalStatus.viewMode === 'EDIT'"
          [warnings]="internalStatus.warnings"
          (alignListChangedActive)="alignListChangedActive()"
          (alignRidChangedActive)="alignRidChangedActive($event)"
        >
        </pt-widget-app-summary-v0-warning>

        <pt-widget-app-summary-v0-preview
          [noDataLabel]="noDataLabel"
          [editable]="internalStatus.viewMode === 'EDIT'"
          [data]="currentData"
          [loading]="internalStatus.isLoading"
          [warningLabel]="previewData?.header?.warningLabel"
          [ridMapping]="ridMapping"
          (alignValue)="alignValue([$event])"
          (noteChangeValue)="changeCurrentValue($event)"
        >
        </pt-widget-app-summary-v0-preview>

        <pt-widget-error
          *ngIf="internalStatus.viewMode === 'ERROR'"
          [errorMessage]="internalStatus.errorMessage | async"
        >
        </pt-widget-error>

        <pt-widget-footer> </pt-widget-footer>
      </ng-container>
    </div>
  `,
})
export class WidgetAppSummaryV0Component extends AbstractWidgetDirective<WidgetAppSummaryV0PreviewDto> {
  currentData: WidgetAppSummaryV0PreviewDto;
  currentNote: string;
  ridMapping: WidgetAppSummaryV0RidMappingDto[];
  currentAppValue: WidgetAppSummaryV0ValueAppDto[] = [];

  constructor(
    public store: Store,
    public actionsSubject: ActionsSubject,
    public translateService: TranslateService,
    public widgetDialogService: WidgetDialogService,
    public service: QueryService,
    public permissionService: NgxPermissionsService
  ) {
    super(
      store,
      actionsSubject,
      translateService,
      widgetDialogService,
      permissionService
    );
  }

  static generateDifferentWidgetValue(
    widgetValue: WidgetAppSummaryV0ValueDto,
    queryResponseValue: WidgetAppSummaryV0QueryResponseDto[]
  ): WidgetAppSummaryV0ResponseValue[] {
    const response: WidgetAppSummaryV0ResponseValue[] = [];
    queryResponseValue.forEach(
      (queryValue: WidgetAppSummaryV0QueryResponseDto) => {
        const currentWidgetValue: WidgetAppSummaryV0ValueAppDto | undefined =
          widgetValue?.app?.find((wValue) => {
            return wValue.applicationUuid === queryValue.uuid;
          });
        if (currentWidgetValue) {
          if (this.isDifferent(queryValue, currentWidgetValue)) {
            response.push({
              id: queryValue.id,
              uuid: queryValue.uuid,
              name: queryValue.name,
              appAssessmentUnsupported: queryValue.appAssessmentUnsupported,
              assessmentCategory: queryValue.assessmentCategory,
              missingRIDValues: queryValue.missingRIDValues,
              scenarioRImplValue: currentWidgetValue.scenarioRImplValue,
              scenarioIImplValue: currentWidgetValue.scenarioIImplValue,
              scenarioDImplValue: currentWidgetValue.scenarioDImplValue,
              scenarioRLevel: currentWidgetValue.scenarioRLevel,
              scenarioILevel: currentWidgetValue.scenarioILevel,
              scenarioDLevel: currentWidgetValue.scenarioDLevel,
            });
          }
        }
      }
    );
    return response;
  }

  static generateWidgetValue(
    widgetValue: WidgetAppSummaryV0ValueDto,
    queryResponseValue: WidgetAppSummaryV0QueryResponseDto[]
  ): WidgetAppSummaryV0ResponseValue[] {
    const response: WidgetAppSummaryV0ResponseValue[] = [];
    queryResponseValue.forEach(
      (queryValue: WidgetAppSummaryV0QueryResponseDto) => {
        const currentWidgetValue: WidgetAppSummaryV0ValueAppDto | undefined =
          widgetValue?.app?.find((wValue) => {
            return wValue.applicationUuid === queryValue.uuid;
          });
        if (currentWidgetValue) {
          response.push({
            id: queryValue.id,
            uuid: queryValue.uuid,
            name: queryValue.name,
            assessmentCategory: queryValue.assessmentCategory,
            missingRIDValues: queryValue.missingRIDValues,

            appAssessmentUnsupported: currentWidgetValue.unsupported,
            scenarioRImplValue: currentWidgetValue.scenarioRImplValue,
            scenarioIImplValue: currentWidgetValue.scenarioIImplValue,
            scenarioDImplValue: currentWidgetValue.scenarioDImplValue,
            scenarioRLevel: currentWidgetValue.scenarioRLevel,
            scenarioILevel: currentWidgetValue.scenarioILevel,
            scenarioDLevel: currentWidgetValue.scenarioDLevel,
          });
        }
      }
    );
    return response;
  }

  static generateRemovedWidgetValue(
    widgetValue: WidgetAppSummaryV0ValueDto,
    queryResponseValue: WidgetAppSummaryV0QueryResponseDto[]
  ): WidgetAppSummaryV0ResponseValue[] {
    const response: WidgetAppSummaryV0ResponseValue[] = [];
    queryResponseValue.forEach(
      (queryValue: WidgetAppSummaryV0QueryResponseDto) => {
        const currentWidgetValue: WidgetAppSummaryV0ValueAppDto | undefined =
          widgetValue?.app?.find((wValue) => {
            return wValue.applicationUuid === queryValue.uuid;
          });
        if (!currentWidgetValue && queryValue.treatmentSelected) {
          response.push({
            id: queryValue.id,
            uuid: queryValue.uuid,
            name: queryValue.name,
            appAssessmentUnsupported: queryValue.appAssessmentUnsupported,
            assessmentCategory: queryValue.assessmentCategory,
            missingRIDValues: queryValue.missingRIDValues,
            scenarioRImplValue: queryValue.scenarioRImplValue,
            scenarioIImplValue: queryValue.scenarioIImplValue,
            scenarioDImplValue: queryValue.scenarioDImplValue,
            scenarioRLevel: queryValue.scenarioRLevel,
            scenarioILevel: queryValue.scenarioILevel,
            scenarioDLevel: queryValue.scenarioDLevel,
          });
        }
      }
    );
    return response;
  }

  loadWidget(widget: UiWidget): void {
    this.dispatchInitState();
    const args = widget.args as WidgetAppSummaryV0ArgsDto;
    this.service.resolve(args.query).subscribe(
      (queryResponse: unknown[]) => {
        const mapping = widget.args.query.mapping;

        const mappedResponse: WidgetAppSummaryV0QueryResponseDto[] =
          WidgetUtils.mappingQueryResponse(
            queryResponse,
            mapping
          ) as WidgetAppSummaryV0QueryResponseDto[];
        const header: WidgetAppSummaryV0ArgsHeader = widget.args.header;
        const widgetValue: WidgetAppSummaryV0ValueDto =
          widget.value as WidgetAppSummaryV0ValueDto;

        const mappedWidgetValue: WidgetAppSummaryV0ResponseValue[] =
          WidgetAppSummaryV0Component.generateWidgetValue(
            widgetValue,
            mappedResponse
          );

        const differenceMappedWidgetValue: WidgetAppSummaryV0ResponseValue[] =
          WidgetAppSummaryV0Component.generateDifferentWidgetValue(
            widgetValue,
            mappedResponse
          );

        const removedWidgetValue: WidgetAppSummaryV0ResponseValue[] =
          WidgetAppSummaryV0Component.generateRemovedWidgetValue(
            widgetValue,
            mappedResponse
          );

        this.currentAppValue = widgetValue?.app || [];

        this.currentData = {
          queryValues: mappedResponse,
          widgetValue: mappedWidgetValue,
          widgetValueWithDifference: differenceMappedWidgetValue,
          removedWidgetValue: removedWidgetValue,
          widgetValueAppUUID: (widgetValue?.app || []).map(
            (a) => a.applicationUuid
          ),
          note: this.currentNote || widgetValue?.note,
          header: header,
        };
        this.currentNote = widgetValue?.note;
        this.checkValidity();
        this.dispatchCompleteState(this.currentData);
      },
      () => {
        this.dispatchErrorState();
      }
    );

    this.service.resolve(args.queryLevels).subscribe(
      (queryResponse: unknown[]) => {
        const mapping = widget.args.queryLevels.mapping;
        const mappedResponse: WidgetAppSummaryV0RidMappingDto[] =
          WidgetUtils.mappingQueryResponse(
            queryResponse,
            mapping
          ) as WidgetAppSummaryV0RidMappingDto[];
        this.ridMapping = mappedResponse;
      },
      () => {
        this.dispatchErrorState();
      }
    );
  }

  saveWidget(ignoreTrigger?: boolean, resetWarning?: TreatmentWarning[]): void {
    const widget = JSON.parse(JSON.stringify(this.widget)) as UiWidget;
    const newValue: WidgetAppSummaryV0ValueDto = {
      app: this.currentAppValue,
      note: this.currentNote,
    };
    widget.value = newValue;
    this.currentData = JSON.parse(JSON.stringify(this.currentData));
    this.currentData.note = this.currentNote;
    this.checkValidity();
    this.completeSaveWidget(widget, ignoreTrigger, resetWarning);
  }

  changeCurrentValue($event: string) {
    this.currentNote = $event;
    this.checkValidity();
    if (
      this.internalStatus.isAutoSave &&
      !this.internalStatus.isLoading &&
      this.internalStatus.isDraft
    ) {
      this.saveWidget();
    }
  }

  configuration(): WidgetConfiguration {
    return { supportAutoSave: true, supportValidation: false };
  }

  private checkValidity() {
    this.isValid$.next(true);
  }

  alignListChangedActive() {
    const widgetValueApp: WidgetAppSummaryV0ValueAppDto[] =
      (this.widget.value as WidgetAppSummaryV0ValueDto)?.app || [];
    const queryValues: WidgetAppSummaryV0QueryResponseDto[] =
      this.currentData.queryValues.filter((q) => q.treatmentSelected);
    const newValueApp: WidgetAppSummaryV0ValueAppDto[] =
      WidgetAppSummaryUtils.alignWidgetApp(widgetValueApp, queryValues);

    const alignedWidgetValue: WidgetAppSummaryV0ValueDto = {
      note: this.currentNote,
      app: newValueApp,
    };

    const alignedWidget: UiWidget = JSON.parse(JSON.stringify(this.widget));
    alignedWidget.value = alignedWidgetValue;
    this.completeSaveWidget(alignedWidget, true, [
      TreatmentWarning.APP_LIST_CHANGED,
    ]);
  }

  alignRidChangedActive(align: boolean) {
    if (align) {
      const appUUID: string[] =
        this.currentData?.widgetValueWithDifference?.length > 0
          ? this.currentData.widgetValueWithDifference.map((d) => d.uuid)
          : [];
      this.alignValue(appUUID);
    } else {
      this.alignValue([]);
      this.completeSaveWidget(
        JSON.parse(JSON.stringify(this.widget)) as UiWidget,
        true,
        [TreatmentWarning.RID_CHANGED]
      );
    }
  }

  alignValue(appUUID: string[]) {
    const resetRidChanged =
      this.currentData.widgetValueWithDifference.length === appUUID.length; // era l'ultima differenza;
    const widget = JSON.parse(JSON.stringify(this.widget)) as UiWidget;

    const widgetValue: WidgetAppSummaryV0ValueDto =
      (widget.value as WidgetAppSummaryV0ValueDto) || {
        app: [],
      };
    widgetValue.note = this.currentNote;
    widgetValue.app = widgetValue?.app?.map((a) => {
      const currentApp = this.currentData?.queryValues?.find((a) =>
        appUUID.includes(a.uuid)
      );
      if (currentApp && appUUID.includes(a.applicationUuid)) {
        console.log('Allinea ' + a.unsupported + ' in ' + currentApp.appAssessmentUnsupported);
        return {
          applicationUuid: a.applicationUuid,
          unsupported: currentApp.appAssessmentUnsupported,
          scenarioRImplValue: currentApp.scenarioRImplValue,
          scenarioIImplValue: currentApp.scenarioIImplValue,
          scenarioDImplValue: currentApp.scenarioDImplValue,
          scenarioRLevel: currentApp.scenarioRLevel,
          scenarioILevel: currentApp.scenarioILevel,
          scenarioDLevel: currentApp.scenarioDLevel,
        };
      } else {
        return a;
      }

    });
    widget.value = widgetValue;

    this.checkValidity();
    // ignoreTrigger è true altrimenti non propaga il resetRid
    this.completeSaveWidget(
      widget,
      true,
      resetRidChanged ? [TreatmentWarning.RID_CHANGED] : undefined
    );
  }

  public static isDifferent(
    queryValue: WidgetAppSummaryV0QueryResponseDto,
    currentWidgetValue: WidgetAppSummaryV0ValueAppDto
  ) {
    const defValue = -1000000;
    const sourceR = queryValue.scenarioRImplValue || defValue;
    const sourceI = queryValue.scenarioIImplValue || defValue;
    const sourceD = queryValue.scenarioDImplValue || defValue;
    const targetR = currentWidgetValue.scenarioRImplValue || defValue;
    const targetI = currentWidgetValue.scenarioIImplValue || defValue;
    const targetD = currentWidgetValue.scenarioDImplValue || defValue;
    return sourceR !== targetR || sourceI !== targetI || sourceD !== targetD;
  }

  public static isDifferentValue(
    queryValue: WidgetAppSummaryV0QueryResponseDto,
    currentWidgetValue: WidgetAppSummaryV0ResponseValue
  ) {
    if (queryValue.uuid !== currentWidgetValue.uuid) {
      return true;
    }
    if (queryValue.appAssessmentUnsupported !== currentWidgetValue.appAssessmentUnsupported) {
      return true;
    }

    if (queryValue.missingRIDValues !== currentWidgetValue.missingRIDValues) {
      return true;
    }

    const defValue = -1000000;
    const sourceR = queryValue.scenarioRImplValue || defValue;
    const sourceI = queryValue.scenarioIImplValue || defValue;
    const sourceD = queryValue.scenarioDImplValue || defValue;
    const targetR = currentWidgetValue.scenarioRImplValue || defValue;
    const targetI = currentWidgetValue.scenarioIImplValue || defValue;
    const targetD = currentWidgetValue.scenarioDImplValue || defValue;
    return sourceR !== targetR || sourceI !== targetI || sourceD !== targetD;
  }
}
